import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import Slider from 'react-slick'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import BottomMenu from '../components/BottomMenu'
import ContactUs from '../components/ContactUs'
import { getPublicBanners } from '../api/apiService'
import UserContext from '../context/UserContext'

const SEARCH_ALIAS_GROUPS = [
  ['mlbb', 'mobile legends', 'mobile legend', 'mobilelegends', 'bang bang'],
  ['pubg', 'pubg global', 'playerunknown battlegrounds'],
  ['coc', 'clash of clans'],
]

const normalizeSearchText = (value) => value.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim()

const expandSearchAliases = (text) => {
  const normalized = normalizeSearchText(text)
  if (!normalized) {
    return []
  }

  const expanded = new Set([normalized, ...normalized.split(' ')])
  SEARCH_ALIAS_GROUPS.forEach((group) => {
    const normalizedGroup = group.map((item) => normalizeSearchText(item))
    if (normalizedGroup.some((alias) => normalized.includes(alias))) {
      normalizedGroup.forEach((alias) => {
        expanded.add(alias)
        alias.split(' ').forEach((token) => expanded.add(token))
      })
    }
  })

  return Array.from(expanded).filter(Boolean)
}

export default function Home() {
  const navigate = useNavigate()
  const location = useLocation()
  const infoData = useSelector((state) => state.infoData)
  const [primaryBanners, setPrimaryBanners] = useState([])
  const [isGamesSearchOpen, setIsGamesSearchOpen] = useState(false)
  const [gameSearch, setGameSearch] = useState('')
  const gamesSectionRef = useRef(null)
  const searchInputRef = useRef(null)
  const { headerSearch, setHeaderSearch } = useContext(UserContext)

  const activeSearch = (isGamesSearchOpen ? gameSearch : '').trim() || headerSearch.trim()
  const normalizedSearch = activeSearch.toLowerCase()
  const filteredGames = useMemo(() => {
    const availableGames = infoData.availableGames || []

    if (!normalizedSearch) {
      return availableGames
    }

    const terms = expandSearchAliases(normalizedSearch)

    return availableGames
      .filter((game) => {
        const searchableText = `${game?.name || ''} ${game?.code || ''}`
        const expandedSearchableText = expandSearchAliases(searchableText).join(' ')
        return terms.every((term) => expandedSearchableText.includes(term))
      })
      .sort((a, b) => {
        const aName = (a?.name || '').toLowerCase()
        const bName = (b?.name || '').toLowerCase()
        const aStarts = aName.startsWith(normalizedSearch)
        const bStarts = bName.startsWith(normalizedSearch)

        if (aStarts === bStarts) {
          return aName.localeCompare(bName)
        }

        return aStarts ? -1 : 1
      })
  }, [infoData.availableGames, normalizedSearch])

  const closeGamesSearch = () => {
    setIsGamesSearchOpen(false)
    setGameSearch('')
  }

  const focusGamesSearchInput = () => {
    setIsGamesSearchOpen(true)
    setTimeout(() => {
      const stickyHeader = document.querySelector('header.sticky-top')
      const headerHeight = stickyHeader?.offsetHeight || 110
      const topOffset = gamesSectionRef.current.getBoundingClientRect().top + window.scrollY - headerHeight - 12
      window.scrollTo({ top: Math.max(topOffset, 0), behavior: 'smooth' })
    }, 0)
    setTimeout(() => {
      searchInputRef.current?.focus()
    }, 250)
  }

  useEffect(() => {
    document.title = 'Demon Official Store - Home'
  }, [])

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const data = await getPublicBanners()
        if (data.success && Array.isArray(data.data)) {
          const sorted = data.data.slice().sort((a, b) => a.priority - b.priority)
          setPrimaryBanners(sorted.filter(b => b.type === 'primary banner'))
        }
      } catch (err) {
        console.error('Failed to fetch banners:', err)
      }
    }
    fetchBanners()
  }, [])

  useEffect(() => {
    const shouldFocusGamesSearch = new URLSearchParams(location.search).get('focusGamesSearch') === '1'

    if (shouldFocusGamesSearch) {
      focusGamesSearchInput()
      navigate('/', { replace: true })
    }
  }, [location.search, navigate])

  useEffect(() => {
    const handleBottomSearchPress = () => {
      focusGamesSearchInput()
    }

    window.addEventListener('dos:focus-games-search', handleBottomSearchPress)
    return () => window.removeEventListener('dos:focus-games-search', handleBottomSearchPress)
  }, [])

  const settings = {
    dots: true,
    centerMode: true,
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 2000,
    arrows: false,
    slidesToShow: 1,
    swipe: true,
    swipeToSlide: true,
    centerPadding: '160px',
    slidesToScroll: 1,
    padding: '0',
    slideMargin: '0',
    focusOnSelect: true,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
          centerPadding: '40px',
          slidesToScroll: 1,
          initialSlide: 1,
        },
      },
      {
        breakpoint: 800,
        settings: {
          slidesToShow: 1,
          centerPadding: '0px',
          slidesToScroll: 1,
          initialSlide: 1,
        },
      },
      {
        breakpoint: 680,
        settings: {
          slidesToShow: 1,
          centerPadding: '0px',
          slidesToScroll: 1,
        },
      },
    ],
  }


  return (
    <section className=''>
      <div className='container'>
        <div className='row pb-2'>
          <div className='col-12'>
            <div className='center-slider'>
              <Slider {...settings}>
                {primaryBanners.map((banner, index) => (
                  <div
                    key={banner._id || index}
                    onClick={() => banner.url && window.open(banner.url, '_blank', 'noopener,noreferrer')}
                    style={{ cursor: banner.url ? 'pointer' : 'default' }}
                  >
                    <img src={banner.image} alt={banner.title || 'banner'} />
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        </div>
      </div>

      <div className='container py-md-4 pb-3' ref={gamesSectionRef}>
        <div className='row'>
          <div className='col-12 pb-2'>
            <div className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
              <h2 className='populargame-heading mb-0'>Popular Games</h2>
              <div className='d-flex align-items-center gap-2'>
                <p className='games-result-count mb-0'>{filteredGames.length} games available</p>
                {!isGamesSearchOpen && (
                  <button type='button' className='games-search-toggle-btn' onClick={focusGamesSearchInput}>
                    Search
                  </button>
                )}
              </div>
            </div>
          </div>
          {isGamesSearchOpen && (
            <div className='col-12 pb-md-4 pb-md-3 pb-2'>
              <div className='home-games-search'>
                <svg className='icon games-search-icon'>
                  <use href='#icon_search'></use>
                </svg>
                <input
                  ref={searchInputRef}
                  type='search'
                  className='form-control games-search-input'
                  placeholder='Search by game name...'
                  value={gameSearch}
                  onChange={(event) => setGameSearch(event.target.value)}
                  aria-label='Search games'
                />
                {gameSearch ? (
                  <button type='button' className='games-search-clear' onClick={() => setGameSearch('')} aria-label='Clear game search'>
                    Clear
                  </button>
                ) : (
                  <button type='button' className='games-search-close' onClick={closeGamesSearch} aria-label='Close game search'>
                    Close
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <div className='row row-cols-3 row-cols-lg-4 g-md-4 g-3 PopularGames'>
          {filteredGames.map((element, index) => {
            const inactive = element.status === 'inactive'
            return (
              <React.Fragment key={element._id || index}>
                <div className='col'>
                  <div
                    className='card'
                    onClick={inactive ? undefined : () => navigate('/game/' + element._id)}
                    style={inactive ? { cursor: 'not-allowed' } : undefined}
                    aria-disabled={inactive || undefined}
                  >
                    <div className='img-box' style={{ position: 'relative' }}>
                      <img
                        src={element.image}
                        alt={element.name}
                        style={inactive ? { filter: 'grayscale(1)', opacity: 0.55 } : undefined}
                      />
                      {inactive && <span className='game-unavailable-badge'>Unavailable</span>}
                    </div>
                    <div className='card-body pb-0'>
                      <h5 className='card-title text-center' style={inactive ? { color: '#9aa0a6' } : undefined}>
                        {element.name}
                      </h5>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            )
          })}
        </div>
        {!filteredGames.length && (
          <div className='games-empty-state'>
            <span className='games-empty-icon'>
              <svg className='icon'>
                <use href='#icon_search'></use>
              </svg>
            </span>
            <h5>No game found for "{activeSearch}"</h5>
            <p>We couldn't match that with any game in our catalogue. Try a shorter keyword, check the spelling, or use a popular short name like MLBB or PUBG.</p>
            <button type='button' className='btn btn-primary rounded-pill px-4' onClick={() => { setGameSearch(''); setHeaderSearch('') }}>
              Reset Search
            </button>
          </div>
        )}
      </div>

      <div className='container py-md-3 pb-3'>
        <div className='row'>
          <div className='col-12 pb-md-4 pb-md-3 pb-2 pt-2'>
            <h2 className='populargame-heading'>Rewards Center</h2>
          </div>
        </div>
        <div className='row row-cols-3 row-cols-lg-4 g-md-4 g-3 PopularGames'>
          <div className='col'>
            <div className='card' onClick={() => navigate('/')}>
              <div className='img-box'>
                <img src='/images/free-diamond.png' alt='popular-games' />
              </div>
              <div className='card-body pb-0'>
                <h5 className='card-title text-center'>Giveaway Redemption</h5>
              </div>
            </div>
          </div>
        </div>

        <div className='row pt-md-4 pt-3'>
          <div className='col-12 pb-md-4 pb-2 pt-3'>
            <h2 className='populargame-heading'>DEMON OFFICIAL STORE</h2>
          </div>
          <div className='col-12'>
            <h3 className='OfficialStore-heading top-up'>Why top-up on Demon Official Store?</h3>
            <p className='official-paragraph para'>
              Join the ranks of millions of passionate gamers who turn to Demon Official Store each month to revolutionize their in-game experience. As your ultimate platform for purchasing game credits and vouchers, Demon Official Store takes
              convenience, speed, and security to unprecedented heights.
            </p>
          </div>

          <div className='row row-cols-1 row-cols-md-2 g-md-4 g-3 pt-0 mt-0 facility'>
            <div className='col'>
              <div className='content-icon'>
                <div className='icon-box'>
                  <img src='/images/fast.png' alt='icon' />
                </div>
                <div className='paragraph'>
                  <h6>Easy and Fast</h6>
                  <p>It only takes few seconds to complete a purchases on Demon Official Store.</p>
                </div>
              </div>
            </div>
            <div className='col'>
              <div className='content-icon'>
                <div className='icon-box'>
                  <img src='/images/delivery.png' alt='icon' />
                </div>
                <div className='paragraph'>
                  <h6>Instant Delivery</h6>
                  <p>When you top-up on Demon Official Store, your purchases is delivered to your game account as soon as payment is completed.</p>
                </div>
              </div>
            </div>
            <div className='col'>
              <div className='content-icon'>
                <div className='icon-box'>
                  <img src='/images/wallet.png' alt='icon' />
                </div>
                <div className='paragraph'>
                  <h6>Convenient Payment Methods</h6>
                  <p>To ensure your convenience, we are using safe payment methods.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ContactUs />
        <BottomMenu />
      </div>
    </section>
  )
}
